import { useEffect, useRef } from 'react';
import type { RenderedPage } from '@/lib/preview/pdfRender.worker';

/** The rendered pages of a PDF, as pixels.
 *
 *  Each page is a bitmap painted into a canvas: no viewer, no text layer, no
 *  annotation layer, so a link drawn over an image is a picture of a link. A
 *  canvas also cannot be selected, dragged as a URL or opened in a new tab,
 *  which closes the routes around a plain "no anchors" rule. */

interface Props {
  pages: RenderedPage[];
  /** The real page count, so a truncated render says so. */
  totalPages: number;
}

export default function PdfPages({ pages, totalPages }: Props) {
  const shown = pages.length;

  return (
    <>
      {/* Bounded and scrollable: stacked inline, ten pages push the file tree
          and the submit button off-screen. The bound is a viewport fraction
          rather than a page count - what matters is how much screen the preview
          may take, and aspect ratio belongs to the document. */}
      <div
        className="max-h-[60vh] space-y-3 overflow-y-auto overscroll-contain rounded border border-ink-800 bg-ink-950/40 p-3"
        // Reachable and announced without a mouse.
        tabIndex={0}
        role="group"
        aria-label={`Rendered pages, ${shown} of ${totalPages}`}
      >
        {pages.map((page) => (
          <PageCanvas key={page.pageNumber} page={page} />
        ))}
      </div>

      {/* Outside the scroller: you should not have to reach the end to learn
          that pages are missing. */}
      {shown < totalPages && (
        <p className="mt-2 text-mini text-ink-500">
          Showing {shown} of {totalPages} pages. The rest are not rendered.
        </p>
      )}
    </>
  );
}

function PageCanvas({ page }: { page: RenderedPage }) {
  const ref = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    // drawImage rather than transferFromImageBitmap: the hook owns the bitmap
    // and transferring would consume it, so a re-render would find nothing.
    ctx.drawImage(page.bitmap, 0, 0);
  }, [page]);

  return (
    <figure className="overflow-hidden rounded border border-ink-800">
      <canvas
        ref={ref}
        width={page.width}
        height={page.height}
        className="block h-auto w-full"
        aria-label={`Page ${page.pageNumber}`}
        role="img"
      />
      {/* Below the page, not over it: a badge on the canvas would cover the
          content this pane exists to show. */}
      <figcaption className="border-t border-ink-800 bg-ink-900 px-2 py-1 text-mini text-ink-500">
        Page {page.pageNumber}
      </figcaption>
    </figure>
  );
}
